// Flowbite-based Pipeline Overview component - Modern Tailwind dashboard

import React, { Suspense, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Users,
  Clock,
  RefreshCw,
  Download
} from 'lucide-react';
import { usePipelineMetrics, usePipelinePerformance, usePipelineOpportunities } from '../../hooks/usePipelineData';
import { MetricCard } from './MetricCard';
import { TrendChart } from '../charts/TrendChart';
import type { PipelineMetrics } from '../../types/salesforce';

interface PipelineOverviewProps {
  className?: string;
  onMetricClick?: (metric: string) => void;
  refreshInterval?: number;
}

export const PipelineOverview: React.FC<PipelineOverviewProps> = ({
  className = '',
  onMetricClick,
  refreshInterval = 300000
}) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Pipeline Overview</h1>

        <div className="flex items-center space-x-3">
          <RefreshIndicator refreshInterval={refreshInterval} />
          <ExportButton />
        </div>
      </div>

      <Suspense fallback={<PipelineLoadingSkeleton />}>
        <PipelineContent onMetricClick={onMetricClick} />
      </Suspense>
    </div>
  );
};

// Main content component
const PipelineContent: React.FC<{ onMetricClick?: (metric: string) => void }> = ({
  onMetricClick
}) => {
  const { data: metrics, isLoading, error } = usePipelineMetrics();
  const { data: performance, isLoading: isPerformanceLoading } = usePipelinePerformance();

  if (isLoading) return <PipelineLoadingSkeleton />;
  if (error) return <ErrorDisplay error={error} />;

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Pipeline Value"
          value={metrics?.totalValueFormatted || '$0'}
          trend={performance?.growth.totalValue}
          icon={<DollarSign className="h-6 w-6" />}
          onClick={() => onMetricClick?.('totalValue')}
          loading={isPerformanceLoading}
          variant="success"
        />

        <MetricCard
          title="Total Opportunities"
          value={metrics?.totalOpportunities.toString() || '0'}
          trend={performance?.growth.totalOpportunities}
          icon={<Target className="h-6 w-6" />}
          onClick={() => onMetricClick?.('totalOpportunities')}
          loading={isPerformanceLoading}
        />

        <MetricCard
          title="Win Rate"
          value={metrics?.winRateFormatted || '0%'}
          trend={performance?.growth.winRate}
          icon={performance?.growth.winRate && performance.growth.winRate > 0 ?
            <TrendingUp className="h-6 w-6" /> :
            <TrendingDown className="h-6 w-6" />
          }
          onClick={() => onMetricClick?.('winRate')}
          loading={isPerformanceLoading}
          variant={performance?.growth.winRate && performance.growth.winRate > 0 ? 'success' : 'warning'}
        />

        <MetricCard
          title="Avg Deal Size"
          value={metrics?.averageDealSizeFormatted || '$0'}
          trend={performance?.growth.averageDealSize}
          icon={<Users className="h-6 w-6" />}
          onClick={() => onMetricClick?.('averageDealSize')}
          loading={isPerformanceLoading}
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Pipeline Trend Chart */}
        <div className="xl:col-span-2">
          <div className="bg-white rounded-lg shadow border h-96">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Pipeline Trend</h2>
              <Suspense fallback={<ChartLoadingSkeleton />}>
                <PipelineTrendChart />
              </Suspense>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="xl:col-span-1">
          <QuickActions />
        </div>
      </div>
    </div>
  );
};

// Pipeline trend chart
const PipelineTrendChart: React.FC = () => {
  const { computedData, isLoading, error } = usePipelineOpportunities();

  if (isLoading) return <ChartLoadingSkeleton />;
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <span className="font-medium text-red-800">Failed to load chart data!</span> <span className="text-red-700">Please try again later.</span>
    </div>
  );
  if (!computedData?.timeSeriesData) return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <span className="font-medium text-blue-800">No data available</span> <span className="text-blue-700">Check your filters or data source.</span>
    </div>
  );

  return (
    <div className="h-64">
      <TrendChart
        data={computedData.timeSeriesData}
        xAxisKey="date"
        yAxisKey="value"
        height={250}
        color="#3B82F6"
        formatter={(value: number) =>
          new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }).format(value)
        }
      />
    </div>
  );
};

// Quick actions component
const QuickActions: React.FC = () => {
  const actions = [
    { label: 'Review Stalled Deals', action: 'stalled-deals', urgent: true, color: 'failure' as const },
    { label: 'Update Forecasts', action: 'update-forecasts', urgent: false, color: 'info' as const },
    { label: 'Schedule Team Review', action: 'team-review', urgent: false, color: 'warning' as const },
    { label: 'Generate Report', action: 'generate-report', urgent: false, color: 'success' as const },
  ];

  return (
    <div className="bg-white rounded-lg shadow border h-96">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>

        <div className="space-y-3">
          {actions.map((action) => (
            <button
              key={action.action}
              className={`w-full justify-start flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                action.color === 'failure' ? 'bg-red-100 text-red-800 hover:bg-red-200' :
                action.color === 'info' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                action.color === 'warning' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
              onClick={() => handleQuickAction(action.action)}
            >
              {action.urgent && <Clock className="h-4 w-4 mr-2" />}
              {action.label}
            </button>
          ))}
        </div>

        {/* Activity Summary */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Today's Activity</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Calls Made</span>
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">12</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Emails Sent</span>
              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">8</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Meetings</span>
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">3</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Refresh indicator
const RefreshIndicator: React.FC<{ refreshInterval: number }> = ({ refreshInterval }) => {
  const [lastRefresh, setLastRefresh] = React.useState(new Date());
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIsRefreshing(true);
      setTimeout(() => {
        setLastRefresh(new Date());
        setIsRefreshing(false);
      }, 1000);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  return (
    <div className="flex items-center space-x-2 text-sm text-gray-500">
      <div className={`h-2 w-2 rounded-full ${isRefreshing ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
      <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
    </div>
  );
};

// Export button
const ExportButton: React.FC = () => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate file download
      const link = document.createElement('a');
      link.href = 'data:text/csv;charset=utf-8,Pipeline Report...';
      link.download = `pipeline-report-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-2 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Exporting...
        </>
      ) : (
        <>
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </>
      )}
    </button>
  );
};

// Loading skeletons
const PipelineLoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="animate-pulse">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2">
        <div className="bg-white rounded-lg shadow border h-96">
          <div className="p-6">
            <div className="animate-pulse h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
      <div className="xl:col-span-1">
        <div className="bg-white rounded-lg shadow border h-96">
          <div className="p-6">
            <div className="animate-pulse space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ChartLoadingSkeleton: React.FC = () => (
  <div className="h-64 bg-gray-100 rounded animate-pulse flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
  </div>
);

// Error display
const ErrorDisplay: React.FC<{ error: any }> = ({ error }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
    <div>
      <span className="font-medium text-red-800">Something went wrong!</span>
      <div className="mt-2 text-sm text-red-700">
        {error?.message || "An unexpected error occurred. Please try again."}
      </div>
    </div>
  </div>
);

// Event handlers
function handleQuickAction(action: string) {
  console.log(`Quick action triggered: ${action}`);

  // Example routing or modal opening
  switch (action) {
    case 'stalled-deals':
      // Navigate to stalled deals view
      break;
    case 'update-forecasts':
      // Open forecast modal
      break;
    case 'team-review':
      // Open calendar integration
      break;
    case 'generate-report':
      // Trigger report generation
      break;
  }
}

export default PipelineOverview;